import {Component, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {Observable, Subject, takeUntil} from 'rxjs';
import {
  Aangifte, AanslagBtw, AanslagVpb, AanslagZwv,
  BerichtenService, BerichtSbt,
  BokkzAlgDataService,
  BokkzClient, BtwAangifte, OphaalResponse, PdfRequest, PdfRequestOptie, PdfRequestSoort, PdfRequestType,
  ServiceBerichtEntity, Toeslagen, VpbAangifte
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ToastService} from '@snelstart/ui-toast';
import {FormBuilder} from '@angular/forms';
import {SbaComponent} from '../sba/sba.component';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-sba-details',
  templateUrl: './feature-tax-sba-details.component.html',
  styleUrl: './feature-tax-sba-details.component.scss',
  imports: [
    SharedUiButtonModule,
    SharedUiDetailsModule,
    SbaComponent,
    SharedUiTooltipModule
  ]
})
export class FeatureTaxSbaDetailsComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  public serviceBerichtXbrl = {} as string;
  public currentRecord: ServiceBerichtEntity | null = null;

  protected readonly detailsConfig = DetailsConfig.create({
    showArrowButtons: true
  });

  public configSave() {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          setTimeout(() => {
            subscription.next(true);
            subscription.complete();
          }, 200);
        })
    });
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    private injector: Injector,
    public toastService: ToastService,
    public bokkzClient: BokkzClient,
    public fb: FormBuilder
  ) {
  }

  public ngOnInit(): void {
    this.algDataService.serviceBerichtXbrl
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.serviceBerichtXbrl = value;
        }
      });
    this.algDataService.serviceBericht$
      .pipe(takeUntil(this.destroy$))
      .subscribe(record => {
        if (record) {
          this.currentRecord = record;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toonPdf(event: Event): void {
    event.stopPropagation();

    const record = this.currentRecord;
    if (!record || !record.serviceBerichtXbrl || !record.aanslagnummer) {
      return;
    }

    const bokkzClient = this.injector.get(BokkzClient);
    const berichtenService = this.injector.get(BerichtenService);

    const ophaal = this.berichtSoortBepalerVoorPdf(record.serviceBerichtXbrl);
    berichtenService.converteerXbrlToJson(ophaal).subscribe((data) => {
      console.log('data' + JSON.stringify(data));
      const pdfRequest = this.pdfSbaBepaler(data, ophaal);
      const pdfAntwoord = bokkzClient.getPdfRapport(pdfRequest);
      pdfAntwoord.subscribe((pdfStringBase64) => {
        const blob = this.dataURItoBlob(pdfStringBase64);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const belastingSoort = this.getBelastingSoort(record.aanslagnummer!);
        const filenameWithoutExtension = record.clientName + ' - Aanslag ' + belastingSoort + ' ' + record.year;
        link.download = `${filenameWithoutExtension}.pdf`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
      });
    });
  }

  getBelastingSoort(aanslagnummer: string): string {
    const match = aanslagnummer.match(/[A-Za-z]/);
    if (!match) return '';

    const letter = match[0].toUpperCase();

    const belastingMap: { [key: string]: string } = {
      'H': 'Inkomstenbelasting',
      'W': 'Zorgverzekeringswet',
      'V': 'Vennootschapsbelasting',
      'T': 'Toeslagen',
      'B': 'Omzetbelasting',
      'F': 'Omzetbelasting',
      'O': 'Omzetbelasting'
    };

    return belastingMap[letter] || '';
  }

  dataURItoBlob(dataURI: string) {
    if (dataURI != null) {
      const byteString = atob(dataURI.replace(/"/g, ''));
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      return new Blob([int8Array], {type: 'application/zip'});
    } else throw 'No dataobject';
  }

  berichtSoortBepalerVoorPdf(xbrl: string): OphaalResponse {
    let ophaalResponse = {} as OphaalResponse;
    if (xbrl.includes('bd-rpt-ih-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'IB'
      };
    } else if (xbrl.includes('bd-rpt-zvw-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'ZVW'
      };
    } else if (xbrl.includes('bd-rpt-vpb-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'VPB'
      };
    } else if (xbrl.includes('bd-rpt-tsl-sbt')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'TSG'
      };
    } else if (xbrl.includes('bd-rpt-ihz-via')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'VIA'
      };
    } else if (xbrl.includes('bd-rpt-ob-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'OB'
      };
    } else if (xbrl.includes('uitstel-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'BECON'
      };
    }
    return ophaalResponse;
  }

  pdfSbaBepaler(data: Aangifte | Toeslagen | VpbAangifte | BtwAangifte, ophaal: OphaalResponse): PdfRequest {
    const pdfRequest = {} as PdfRequest;
    if (ophaal.berichtsoort.includes('IB')) {
      pdfRequest.aanslagIbJaar1 = data;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
    } else if (ophaal.berichtsoort.includes('ZVW')) {
      pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.ZVW;
    } else if (ophaal.berichtsoort.includes('VPB')) {
      pdfRequest.aanslagVpbJaar1 = data as AanslagVpb;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.VPB;
    } else if (ophaal.berichtsoort.includes('TSG')) {
      pdfRequest.toeslagenJaar1 = data as BerichtSbt;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Beschikking;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.TSL;
    } else if (ophaal.berichtsoort.includes('OB')) {
      pdfRequest.aanslagBtwJaar1 = data as AanslagBtw;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.BTW;
    } else if (ophaal.berichtsoort.includes('VIA')) {
      pdfRequest.aangifteJaar1 = data as Aangifte;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Via;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
    } else if (ophaal.berichtsoort.includes('BECON')) {
      pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
      pdfRequest.concept = false;
    }
    return pdfRequest;
  }

  public isGecontroleerd(): boolean {
    if (!this.currentRecord?.id) {
      return false;
    }
    return this.algDataService.isGecontroleerd(this.currentRecord.id);
  }

  public saveForm(event: Event): void {
    event.stopPropagation();

    const record = this.currentRecord;
    if (!record) {
      return;
    }

    const nieuweStatus = !this.isGecontroleerd();

    const serviceBerichtEntity = {
      ...record,
      gecontroleerd: nieuweStatus
    } as ServiceBerichtEntity;

    this.bokkzClient.postOneSba(serviceBerichtEntity).subscribe(value => {
      if (nieuweStatus) {
        this.algDataService.markeerAlsGecontroleerd(record.id!);
        this.toastService.showSuccessMessage('Het bericht is gemarkeerd als "Gecontroleerd".');
      } else {
        this.algDataService.markeerAlsNietGecontroleerd(record.id!);
        this.toastService.showInfoMessage('Het bericht is gemarkeerd als "Te controleren".');
      }
    });
  }

}
